import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../utils/api';

interface User {
  id: string;
  email: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, username: string, password: string, displayName?: string) => Promise<void>;
  logout: () => void;
  initialize: () => void;
  updateUser: (user: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,

      initialize: () => {
        const { token } = get();
        if (token) {
          set({ isAuthenticated: true });
          // Fetch user data
          api.get('/auth/me').then((res: any) => {
            set({ user: res.user });
          }).catch(() => {
            get().logout();
          });
        }
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const res: any = await api.post('/auth/login', { email, password });
          set({ 
            token: res.token, 
            user: res.user,
            isAuthenticated: true 
          });
        } finally {
          set({ isLoading: false });
        }
      },

      register: async (email: string, username: string, password: string, displayName?: string) => {
        set({ isLoading: true });
        try {
          const res: any = await api.post('/auth/register', { 
            email, 
            username, 
            password, 
            displayName 
          });
          set({ 
            token: res.token, 
            user: res.user,
            isAuthenticated: true 
          });
        } finally {
          set({ isLoading: false });
        }
      },

      logout: () => {
        set({ 
          token: null, 
          user: null, 
          isAuthenticated: false 
        });
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...userData } });
        }
      },
    }),
    {
      name: 'legion-auth',
      partialize: (state) => ({ token: state.token }),
    }
  )
);
